/**
 * Files API service using the centralized API client
 * Uses api/v1 prefix for files endpoints
 */
import api, { crmApiUrl } from '@crm-src/lib/api'
import { saveBlob } from '../../lib/download'

// Types
export interface FileItem {
  id: string
  name: string
  is_folder: boolean
  file_size?: number
  file_size_formatted?: string
  content_type?: string
  path: string
  parent_id?: string
  has_children: boolean
  can_be_deleted: boolean
  created_at: string
  updated_at: string
  created_by: {
    id: string
    name: string
  } | null
  full_path?: string
  ancestors?: FileItem[]
  download_url?: string
  updated_by?: {
    id: string
    name: string
  }
}

export interface BreadcrumbItem {
  id: string
  name: string
  is_folder: boolean
}

interface FilesListResponse {
  files: FileItem[]
  pagination: {
    current_page: number
    per_page: number
    total_count: number
  }
  breadcrumb: BreadcrumbItem[]
}

interface FileResponse {
  file: FileItem
  message?: string
}

export interface FilesListParams {
  parent_id?: string
  search?: string
  type?: 'folders' | 'files'
  /**
   * Server sort column — one of `FILES_TABLE.columns[].key`
   * (name | size | modified | created_by), allow-listed by
   * `Api::V1::FilesController.sortable_columns`. A `string`, not a union,
   * because `buildTableParams` types it as one: the union lives in
   * `components/table/configs.ts` and is enforced against the server by
   * `config-parity.test.ts`, so restating it here would be a second place to
   * keep in sync. An undeclared value is harmless — the server keeps its
   * default order.
   *
   * The old union named `created_at` / `updated_at`, neither of which is a
   * column the table renders; `modified` replaces `updated_at`.
   */
  sort?: string
  /** Canonical direction param. `Sortable` also accepts the legacy `direction`. */
  dir?: 'asc' | 'desc'
  direction?: 'asc' | 'desc'
  /** Facet: a comma-separated list of FILE_TYPE_KINDS values. */
  file_type?: string
  /** Facet: a comma-separated list of user ids. */
  created_by_id?: string
  page?: number
  per_page?: number
}

interface CreateFolderParams {
  name: string
  parent_id?: string
}

interface UploadFileParams {
  name?: string
  parent_id?: string
  file: File
}

interface UpdateFileParams {
  name?: string
  attachment?: File
}

export const filesApi = {
  /**
   * Get files and folders with optional filters and pagination
   */
  getAll: async (params?: FilesListParams): Promise<FilesListResponse> => {
    const queryParams = new URLSearchParams()
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value))
        }
      })
    }
    
    const url = `/files${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await api.get(url)
    return response.data
  },

  /**
   * Create a new folder
   */
  createFolder: async (folderData: CreateFolderParams): Promise<FileResponse> => {
    const response = await api.post('/files/create_folder', folderData)
    return response.data
  },

  /**
   * Upload a file
   */
  uploadFile: async (uploadData: UploadFileParams): Promise<FileResponse> => {
    const formData = new FormData()
    formData.append('file', uploadData.file)
    
    if (uploadData.name) {
      formData.append('name', uploadData.name)
    }
    if (uploadData.parent_id) {
      formData.append('parent_id', uploadData.parent_id)
    }
    
    const response = await api.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  /**
   * Update an existing file/folder
   */
  update: async (id: string, updateData: UpdateFileParams): Promise<FileResponse> => {
    if (updateData.attachment) {
      // If updating file attachment, use FormData
      const formData = new FormData()
      formData.append('attachment', updateData.attachment)
      
      if (updateData.name) {
        formData.append('name', updateData.name)
      }
      
      const response = await api.put(`/files/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data
    } else {
      // For name updates only
      const response = await api.put(`/files/${id}`, updateData)
      return response.data
    }
  },

  /**
   * Delete a file or folder
   */
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/files/${id}`)
    return response.data
  },

  /**
   * Download a file
   */
  download: async (id: string): Promise<void> => {
    const response = await api.get(`/files/${id}/download`, {
      responseType: 'blob'
    })
    
    const blob = response.data

    // Try to get filename from response headers
    const contentDisposition = response.headers['content-disposition']
    let filename = 'download'
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }

    // Native OS Save-As dialog in the desktop app; browser download on the web.
    await saveBlob(filename, blob)
  },

  /**
   * Get download URL for a file (for direct access)
   */
  getDownloadUrl: async (id: string): Promise<string> => {
    return crmApiUrl(`/api/v1/files/${id}/download`)
  },

  /**
   * Search files and folders
   */
  search: async (query: string, parentId?: string): Promise<FileItem[]> => {
    const params: FilesListParams = { search: query }
    if (parentId) {
      params.parent_id = parentId
    }
    
    const response = await filesApi.getAll(params)
    return response.files
  },

  /**
   * Get folder contents (files and subfolders)
   */
  getFolderContents: async (folderId?: string): Promise<FileItem[]> => {
    const params: FilesListParams = {}
    if (folderId) {
      params.parent_id = folderId
    }
    
    const response = await filesApi.getAll(params)
    return response.files
  },

  /**
   * Get direct view URL for a file
   */
  getViewUrl: (id: string): string => {
    return crmApiUrl(`/api/v1/files/${id}/view`)
  },

  /**
   * Get authenticated blob URL for viewing a file (images, videos, etc.)
   */
  getViewBlob: async (id: string): Promise<string> => {
    const response = await api.get(`/files/${id}/view`, {
      responseType: 'blob'
    })
    
    return window.URL.createObjectURL(response.data)
  },

  /**
   * Get authenticated blob URL for downloading a file
   */
  getDownloadBlob: async (id: string): Promise<string> => {
    const response = await api.get(`/files/${id}/download`, {
      responseType: 'blob'
    })
    
    return window.URL.createObjectURL(response.data)
  },

  /**
   * Move a file or folder to a different parent folder
   */
  move: async (id: string, newParentId: string | null): Promise<FileResponse> => {
    const response = await api.put(`/files/${id}/move`, { parent_id: newParentId })
    return response.data
  }
}

// Export default for convenience
export default filesApi